import { useEffect } from "react";
import { useForm } from "react-hook-form";
import FormModal from "@/components/FormModal/FormModal";
import CheckIcon from "@/components/SVGComponents/CheckIcon";
import formRenderer from "@/lib/formRenderer";
import type { FormConfig } from "@/lib/formRenderer";
import { notBlankValidator, requiredMessage } from "@/lib/utils";
import styles from "./EnterCodeModal.module.scss";

const CODE_MAX_LENGTH = 100;

const CODE_FIELD: FormConfig = {
  code: {
    controlType: "input",
    label: "Ticket code",
    labelVariant: "caption",
    placeholder: "Enter the code printed under the QR",
    isRequired: true,
    validations: {
      required: requiredMessage("Ticket code"),
      validate: notBlankValidator("Ticket code"),
      maxLength: { value: CODE_MAX_LENGTH, message: `Code must be at most ${CODE_MAX_LENGTH} characters` },
    },
    props: { maxLength: CODE_MAX_LENGTH, autoComplete: "off", autoFocus: true },
  },
};

interface EnterCodeFormValues {
  code: string;
}

export interface EnterCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Receives the trimmed code — same handler a camera scan goes through.
  onSubmit: (code: string) => void;
}

// Fallback for when a member's QR won't scan (damaged screen, glare, etc.).
const EnterCodeModal = ({ isOpen, onClose, onSubmit }: EnterCodeModalProps) => {
  const { register, handleSubmit, reset, formState } = useForm<EnterCodeFormValues>({
    defaultValues: { code: "" },
  });

  useEffect(() => {
    if (isOpen) reset({ code: "" });
  }, [isOpen, reset]);

  const handleFormSubmit = handleSubmit((values) => {
    onSubmit(values.code.trim());
    onClose();
  });

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Enter code manually"
      subtitle="Use this when the member's QR won't scan."
      onSubmit={handleFormSubmit}
      submitLabel="Check in"
      submitIcon={<CheckIcon width={14} height={14} />}
      submitDisabled={formState.isSubmitting}
      footerButtonClassName={styles.footerButton}
    >
      {formRenderer<EnterCodeFormValues>(CODE_FIELD, formState, register)}
    </FormModal>
  );
};

export default EnterCodeModal;
